/**
 * Flatten an arbitrary object to dotted key paths, and filter the result.
 *
 * Lifted verbatim out of `ObjectDisplayTable.tsx` by LOCKER-355 so both it and
 * the SA locker-object viewer share one implementation, and — the reason it
 * moved rather than being copied — so the flattening and the filter can be
 * tested without mounting a MUI `DataGrid`. The DoD asks for a test proving
 * that nested keys appear as dotted paths and that typing narrows on BOTH key
 * path and value; asserting that against rendered grid cells would be a test of
 * the grid, not of the behaviour.
 *
 * Behaviour is unchanged from the original, including the two quirks worth
 * naming because they look like bugs and are not:
 *
 *  1. Arrays become `path[i]` segments rather than a JSON blob, so an
 *     individual element is searchable.
 *  2. A string that parses as JSON is expanded in place. The SL2 partner
 *     payloads store `meta_data` as a JSON *string*, and leaving it opaque
 *     hides the `sl2ReservationId` inside it — which is exactly the field
 *     someone diagnosing a mis-paired door is looking for.
 */

export type FlatRow = { id: number; key: string; value: string }

export const flattenObject = (
  obj: Record<string, any>,
  parentKey = "",
  result: Record<string, any> = {},
) => {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key
      if (Array.isArray(obj[key])) {
        obj[key].forEach((item: any, index: number) => {
          const arrayKey = `${newKey}[${index}]`
          if (typeof item === "object" && item !== null) {
            flattenObject(item, arrayKey, result)
          } else {
            result[arrayKey] = item
          }
        })
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        flattenObject(obj[key], newKey, result)
      } else {
        const value = obj[key]
        if (typeof value === "string") {
          try {
            const parsed = JSON.parse(value)
            if (Array.isArray(parsed)) {
              parsed.forEach((item, index) => {
                const arrayKey = `${newKey}[${index}]`
                if (typeof item === "object" && item !== null) {
                  flattenObject(item, arrayKey, result)
                } else {
                  result[arrayKey] = item
                }
              })
            } else if (typeof parsed === "object" && parsed !== null) {
              flattenObject(parsed, newKey, result)
            } else {
              result[newKey] = parsed
            }
          } catch {
            result[newKey] = value
          }
        } else {
          result[newKey] = value
        }
      }
    }
  }
  return result
}

/** Dotted-path rows, in insertion order, ready for a two-column table. */
export const toFlatRows = (data: Record<string, any>): FlatRow[] => {
  const flat = flattenObject(data)
  return Object.keys(flat).map((key, index) => ({
    id: index,
    key,
    value: String(flat[key]),
  }))
}

/** Case-insensitive substring match on the key path OR the value. */
export const filterFlatRows = (rows: FlatRow[], query: string): FlatRow[] => {
  const q = query.toLowerCase()
  return rows.filter(
    (row) =>
      row.key.toLowerCase().includes(q) || row.value.toLowerCase().includes(q),
  )
}
